'use client';

import { fetchViaProxy } from '@/actions/proxy-fetch';
import Avatar from '@/components/next-ui/Avatar';
import Input from '@/components/next-ui/Input';
import Typography from '@/components/next-ui/Typography';
import { CampaignType } from '@/constants/postType.constants';
import useDebounce from '@/hooks/useDebounce';
import { useIsNonProfitUser } from '@/hooks/useUser';
import useUserProfile from '@/hooks/useUserProfile';
import { getUserCountry } from '@/utils/user.helper';
import Spinner from '@components/Spinner';
import { Combobox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'cva';
import { filter, isEmpty, isNull } from 'lodash-es';
import React, { useState } from 'react';

const minProjects = {
  [CampaignType.FundRaise]: 0,
  [CampaignType.CollectGoods]: 1,
};

const searchNonProfits = async (searchText, country) => {
  if (isEmpty(searchText)) {
    return null;
  }
  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/nonprofits/search?name=${searchText}&country=${country}&includeProjects=true&limit=10`,
    {
      method: 'GET',
    },
  );

  if (!ok) {
    throw new Error('Something went wrong');
  }
  return data.data || [];
};

function NonProfitSearchBox(props) {
  const { value, onChange, campaignType, error, isNekiAdmin } = props;

  const user = useUserProfile();
  const isNonProfitUser = useIsNonProfitUser() || isNekiAdmin;
  let country = isNonProfitUser
    ? user?.activeCommunity?.nonProfit?.country || ''
    : getUserCountry(user);

  const [nonProfitSearchText, setNonProfitSearchText] = useState('');
  let debouncedSearchText = useDebounce(nonProfitSearchText, 500) || '';

  if (isNonProfitUser && !isNekiAdmin) {
    debouncedSearchText = user?.activeCommunity?.nonProfit?.name;
  }

  const resp = useQuery({
    queryKey: [`nonprofits-for-campaigns-${campaignType}`, debouncedSearchText],
    queryFn: async () => {
      const data = await searchNonProfits(debouncedSearchText, country);
      const numMinProjects = minProjects[campaignType];
      const withProjects = numMinProjects === 0 ? data : [];
      if (numMinProjects > 0 && !isEmpty(data)) {
        data.forEach((np) => {
          const campaignProjectCount = isEmpty(np.projects)
            ? 0
            : filter(np.projects, (p) => p.campaignType === campaignType)
                .length;
          if (campaignProjectCount >= numMinProjects) {
            withProjects.push(np);
          }
        });
      }
      return withProjects;
    },
    keepPreviousData: true,
    // enabled: isNonProfitUser ? false : true,
  });

  React.useEffect(() => {
    if (isNonProfitUser) {
      resp.refetch();
    }
  }, []);

  const { data: nonProfitResults, isLoading, isFetching } = resp;
  const isSearchingNonProfits = isLoading || isFetching;
  const nonProfits = nonProfitResults || [];
  return (
    <div>
      <Combobox
        as="div"
        className="space-y-4"
        onChange={(selected) => {
          let selectedValue = null;
          if (isNekiAdmin || !isNonProfitUser) {
            selectedValue = selected;
          } else if (isNonProfitUser && nonProfits.length > 0) {
            selectedValue = nonProfits[0];
          }
          // onChange(
          //   isNonProfitUser && nonProfits.length > 0 ? nonProfits[0] : selected,
          // );
          onChange(selectedValue);
        }}
        value={value}
        by="id">
        {({ open }) => (
          <>
            <div className="relative">
              <Combobox.Input
                as={Input}
                label="Non Profit"
                required
                displayValue={(nonProfit) => nonProfit?.name || ''}
                placeholder="Search Non Profits..."
                className="text-14"
                error={error}
                onChange={(e) => {
                  setNonProfitSearchText(e.target.value);
                }}
              />
              {open && !isNull(nonProfits) && (
                <Combobox.Options
                  as="ul"
                  className="absolute z-10 mt-2 w-full rounded-lg border border-gray-300 bg-white shadow-lg">
                  {isSearchingNonProfits && isEmpty(nonProfits) && (
                    <div className="relative flex w-full items-center justify-start gap-2 px-4 py-2">
                      <Spinner color="primary" size="xs" />
                      <Typography variant="body1">Searching...</Typography>
                    </div>
                  )}
                  {!isSearchingNonProfits && isEmpty(nonProfits) && (
                    <div className="relative flex w-full items-center justify-start px-4 py-2">
                      <Typography variant="body1">
                        No Non Profits found
                      </Typography>
                    </div>
                  )}
                  {nonProfits?.map((nonProfit) => (
                    <Combobox.Option
                      key={nonProfit.id}
                      value={nonProfit}
                      className={({ active }) =>
                        cx(
                          'relative cursor-pointer select-none py-2 pl-4 pr-4 text-text-primary',
                          active && 'bg-primary-light-1',
                        )
                      }>
                      {({ selected, active }) => (
                        <div className="flex items-center justify-start gap-4">
                          <Avatar
                            alt={nonProfit.name}
                            src={nonProfit.logo_url}
                            size="xs"
                            profile="npo"
                          />
                          <Typography variant="body1" className="truncate">
                            {nonProfit.name}
                          </Typography>
                        </div>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}
            </div>
          </>
        )}
      </Combobox>
    </div>
  );
}

export default NonProfitSearchBox;
